import 'maplibre-gl/dist/maplibre-gl.css'
import { useEffect, useRef, useState } from 'react'
import maplibregl from 'maplibre-gl'
import { useSelector } from 'react-redux'
import { useApi } from './useApi'
import { useMapViewAddLeadEnabled } from '@user/store/tenantFeatures/hooks/useMapViewAddLeadEnable'
import { logger } from '@user/utils/logger'
import { HereMapClickEvent } from './useHereMapInstance'
import { getMapLibreStyle } from '@user/components/HereMap/utils/maplibreStyles'
import { preservePinLayers } from './useMapLibreMarkers'
import { userGeneralPreferencesList } from '@user/store/userPreferences/userPreferencesGeneral'
import { getPreferredMapBaseView } from '@user/components/HereMap/mapView'
import { HereMapBaseView } from '@user/components/HereMap/types'
import { fetchMapConfig } from '@user/store/map/api'

interface UseMapLibreInstanceParams {
  containerRef: React.RefObject<HTMLDivElement>
  center?: { lat: number; lng: number }
  zoom?: number
  onMapClick?: (data: HereMapClickEvent | undefined) => void
  locationResolved: boolean
}

export interface UseMapLibreInstanceResult {
  map: maplibregl.Map | null
  mounted: boolean
  contactPinsGroup: null
  knockStatusGroup: null
  currentLocationGroup: null
}

export const useMapLibreInstance = ({
  containerRef,
  center,
  zoom,
  onMapClick,
  locationResolved,
}: UseMapLibreInstanceParams): UseMapLibreInstanceResult => {
  const [map, setMap] = useState<maplibregl.Map | null>(null)
  const [mounted, setMounted] = useState(false)

  const onMapClickRef = useRef(onMapClick)
  const minZoomForAddLeadRef = useRef<number>(16)
  const { getAddressDetailsForMapClick } = useApi()
  const mapViewAddLeadEnabled = useMapViewAddLeadEnabled()

  // Restore the user's saved base-map view (SH-566). The map is initialised with
  // it, and appliedViewRef lets the sync effect below re-apply it if preferences
  // load after the map was created (else the map would stay on the default view).
  const userGeneralPreferences = useSelector(userGeneralPreferencesList)
  const preferredMapBaseView = getPreferredMapBaseView(userGeneralPreferences)
  const appliedViewRef = useRef<HereMapBaseView | null>(null)

  useEffect(() => {
    onMapClickRef.current = onMapClick
  }, [onMapClick])

  // ── Initialization (runs once when locationResolved becomes true) ──────────
  useEffect(() => {
    if (!locationResolved || !containerRef.current) return

    const container = containerRef.current
    let isMounted = true

    appliedViewRef.current = preferredMapBaseView
    const mapInstance = new maplibregl.Map({
      container,
      style: getMapLibreStyle(preferredMapBaseView),
      center: center ? [center.lng, center.lat] : [0, 0],
      zoom: zoom ?? 2,
      attributionControl: false,
      fadeDuration: 0,
    })

    // Min-zoom guard (mirrors HERE Maps implementation).
    // Skip during programmatic animations (flyTo/easeTo) — the zoom-out arc of
    // a long-distance flyTo naturally dips below 3.5, and calling setZoom here
    // would cancel the animation mid-flight.
    mapInstance.on('zoom', () => {
      if (!isMounted) return
      if (mapInstance.isEasing()) return
      if (mapInstance.getZoom() < 3.5) mapInstance.setZoom(3.5)
    })

    // Map click → AddLead flow; skip when a pin symbol layer was clicked
    const handleClick = (evt: maplibregl.MapMouseEvent) => {
      if (!isMounted) return
      const pinHit = mapInstance.queryRenderedFeatures(evt.point, {
        layers: ['contact-pins-layer', 'active-pin-layer'],
      })
      if (pinHit.length > 0) return
      const currentZoom = mapInstance.getZoom()
      if (currentZoom < minZoomForAddLeadRef.current) {
        logger.info(`Zoom too low for add lead. Current: ${currentZoom}`)
        return
      }
      if (mapViewAddLeadEnabled) {
        const { lat, lng } = evt.lngLat
        getAddressDetailsForMapClick({ lat, lng }).then((locationData) => {
          if (!isMounted) return
          if (locationData) onMapClickRef.current?.(locationData)
        })
      }
    }
    mapInstance.on('click', handleClick)

    // Debounced resize — mirrors HERE Maps ResizeObserver pattern
    let resizeTimer: number | null = null
    const scheduleResize = () => {
      if (!isMounted) return
      if (resizeTimer !== null) window.clearTimeout(resizeTimer)
      resizeTimer = window.setTimeout(() => {
        resizeTimer = null
        if (isMounted) mapInstance.resize()
      }, 100)
    }
    const resizeObserver = new ResizeObserver(scheduleResize)
    resizeObserver.observe(container)
    window.addEventListener('resize', scheduleResize)

    // Default arrow cursor; restore grabbing only during drag
    const canvas = mapInstance.getCanvas()
    const setCursor = (cursor: string) => {
      if (isMounted) canvas.style.cursor = cursor
    }
    const onDragStart = () => setCursor('grabbing')
    const onDragEnd = () => setCursor('default')
    mapInstance.on('dragstart', onDragStart)
    mapInstance.on('dragend', onDragEnd)

    mapInstance.once('load', () => {
      if (!isMounted) return
      canvas.style.cursor = 'default'
      setMap(mapInstance)
      setMounted(true)
      fetchMapConfig().then((config) => {
        if (!isMounted || !config) return
        mapInstance.setMaxZoom(config.maxZoom)
        minZoomForAddLeadRef.current = config.minZoomForAddLead
      })
    })

    return () => {
      isMounted = false
      if (resizeTimer !== null) window.clearTimeout(resizeTimer)
      window.removeEventListener('resize', scheduleResize)
      resizeObserver.disconnect()
      mapInstance.off('click', handleClick)
      mapInstance.off('dragstart', onDragStart)
      mapInstance.off('dragend', onDragEnd)
      mapInstance.remove()
      setMap(null)
      setMounted(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationResolved, containerRef])

  // If the saved base-map view resolves after the map was created (preferences
  // loaded late), apply it. Skipped in the common case where the map was already
  // initialised with the correct style (appliedViewRef matches), avoiding a
  // needless style reload/flash.
  useEffect(() => {
    if (!map) return
    if (appliedViewRef.current === preferredMapBaseView) return
    appliedViewRef.current = preferredMapBaseView
    // Preserve the pin sources/layers across the diff so the pins don't flash
    // when this fires after a base-map switch from LayerControls (SH-909).
    map.setStyle(getMapLibreStyle(preferredMapBaseView), {
      diff: true,
      transformStyle: preservePinLayers,
    })
  }, [map, preferredMapBaseView])

  // ── Prop sync ─────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!map || !center) return
    // Don't interrupt a flyTo animation (e.g. Locate Me) with a setCenter from
    // tryBrowserLocation resolving late after the map has already opened.
    if (map.isMoving()) return
    map.setCenter([center.lng, center.lat])
  }, [map, center])

  useEffect(() => {
    if (!map || typeof zoom !== 'number') return
    if (map.isMoving()) return
    map.setZoom(zoom)
  }, [map, zoom])

  return {
    map,
    mounted,
    contactPinsGroup: null,
    knockStatusGroup: null,
    currentLocationGroup: null,
  }
}
